import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { createEmitter } from '../../events';
import { useSelector } from '../../hook-utils';
import { AgentViewTab, CallState, ClidObjectType, Media } from '../../model';
import { actions } from '../../store';
import { getActiveDevice } from '../../store/reducers';
import { selectConfigProp, selectDateFormating, selectMyData, selectMyDevices, selectMyself } from '../../store/reducers/config';
import { getParkedMedia, selectCollapsedPanelValue as selectIsPanelCollapsed } from '../../store/reducers/localSettings';
import { selectHeldCall, selectMedia, selectMyMedia } from '../../store/reducers/media';
import { getNow } from '../../store/reducers/now';
import { selectExtension } from '../../store/reducers/queueAgents';
import { hasTranscription } from '../../store/reducers/transcriptions';
import { agentStatusTitle } from '../AgentsTable';
import Button from '../Button';
import { callStateDescription } from '../CallPopup';
import DropdownButton from '../DropdownButton';
import { ReactComponent as WrapupIcon } from '../icons/callstatus/wrapup.svg';
import { ReactComponent as CollapseIcon } from '../icons/chevron-left.svg';
import { ReactComponent as ExpandIcon } from '../icons/chevron-right.svg';
import { ReactComponent as PhoneCallsIcon } from '../icons/phone-calls.svg';
import { ReactComponent as WarningIcon } from '../icons/warning.svg';
import MediaIcon, { MediaItemIconType } from '../MediaIcon';
import { MediaItem, MediaItemIconTitle } from '../MediaItem';
import MediaMenu, { MediaMenuState } from '../MediaMenu';
import { targetFromEvent } from '../Menu';
import PresenceIcon from '../PresenceIcon';
import { SplitterDivider, StoredSplitter } from '../Splitter';
import { TabButton, TabsHeader } from '../Tabs';
import Timer from '../Timer';
import TitleTag from '../TitleTag';
import CallDetailsPage from './CallDetailsPage';
import { CallView } from './CallView';
import ChatView from './ChatView';
import ContactPage from './ContactPage';
import Dashboard from './Dashboard';
import EmailView from './EmailView';
import FacebookView from './FacebookView';
import './index.scss';
import MediaPage from './MediaPage';
import OutboundView from './OutboundView';
import TasksPage from './TasksPage';
import TranscriptionDetailsPage from './Transcription';
import TweatView from './TweatView';
import WrapupsPage from './WrapupsPage';

const SidebarPanelKey = 'agentViewSidebar'

interface AgentViewParams {
  id?: string
  tab?: AgentViewTab
}

type MediaScriptAppender = (html: string) => void

interface MediaContextData {
  appendScript: MediaScriptAppender
  canAppendScript: boolean
  onAppendScript(handler: MediaScriptAppender): () => void
}

const MediaContext = React.createContext<MediaContextData | null>(null)

export default function AgentView() {
  const { id: mediaId, tab = '' } = useParams<AgentViewParams>() as AgentViewParams
  console.log('Render AgentView ' + mediaId)
  const dispatch = useDispatch()

  const media = useSelector(selectMyMedia)
  const selectedMedia = media.find(m => m.id === mediaId)
  const isCollapsed = useSelector(s => selectIsPanelCollapsed(s, SidebarPanelKey))

  const refMediaId = useRef<string | undefined>(undefined)
  const selectMedia = useCallback((id?: string) => {
    if (id !== mediaId) {
      dispatch(actions.openAgentView(id, tab))
    }
  }, [dispatch, mediaId, tab])

  useEffect(() => {

    if (!mediaId || !selectedMedia) {
      let exists = false
      if (mediaId) {
        exists = media.findIndex(m => m.id === mediaId) > -1
        if (!exists) {
          console.info('not ' + mediaId)
        }
      }

      let a: Media[] = []
      let w: Media[] = []
      if (!exists) {
        a = media.filter(m => (m.mediaType === "CALL" || m.mediaType === "VM") && m.status !== CallState.WaitingForWrapup)
        w = media.filter(m => (m.mediaType === "CALL" || m.mediaType === "VM") && m.status === CallState.WaitingForWrapup)

        if (a.length === 0) {
          a = media.filter(m => (m.mediaType !== "CALL" && m.mediaType !== "VM") && m.status !== CallState.WaitingForWrapup && m.status !== CallState.ExtensionPending)
        }

        if (w.length === 0) {
          w = media.filter(m => (m.mediaType !== "CALL" && m.mediaType !== "VM") && m.status === CallState.WaitingForWrapup)
        }
      }

      const id = mediaId && exists ? mediaId : a.length > 0 ? a[0].id : w.length > 0 ? w[0].id : undefined

      //update id if different
      if (refMediaId.current !== id) {
        refMediaId.current = id
        dispatch(actions.openAgentView(id))
      }
    } else if (mediaId === selectedMedia.id && mediaId !== refMediaId.current) {
      refMediaId.current = mediaId
    }

  }, [dispatch, media, mediaId, selectedMedia])

  const [subscriptionCounter, setSubscriptionCounter] = useState(0)
  const mediaContext = useMemo<MediaContextData>(() => {
    const emitter = createEmitter<[string]>()

    return {
      appendScript(html) {
        emitter.emit(html)
      },
      canAppendScript: subscriptionCounter > 0,
      onAppendScript(handler) {
        setSubscriptionCounter(c => c + 1)
        const unsub = emitter.subscribe(handler)
        return () => {
          unsub()
          setSubscriptionCounter(c => c - 1)
        }
      },
    }
  }, [ subscriptionCounter])

  const sidebar = useMemo(() => {
    return <Sidebar selectedMediaId={mediaId} selectMedia={selectMedia} />
  }, [mediaId, selectMedia])

  const mainPanel = useMemo(() => {
    return <MainPanel tab={tab} mediaId={mediaId} />
  }, [mediaId, tab])

  const render = useMemo(() => {
    if (isCollapsed) {
      return <div className='AgentView'>
        {sidebar}
        <MediaContext.Provider value={mediaContext}>
          {mainPanel}
        </MediaContext.Provider>
      </div>
    }

    return <StoredSplitter className='AgentView' storeKey={SidebarPanelKey} initialValue={0.2}>
      {sidebar}
      <SplitterDivider />
      <MediaContext.Provider value={mediaContext}>
        {mainPanel}
      </MediaContext.Provider>
    </StoredSplitter>
  }, [isCollapsed, mainPanel, mediaContext, sidebar])

  return render
}

export function useMediaContext() {
  const context = useContext(MediaContext)
  return useMemo(() => ({
    appendScript: context ? context.appendScript : () => { },
    canAppendScript: context ? context.canAppendScript : false,
  }), [context])
}

export function useMediaScripts(onAppend: MediaScriptAppender) {
  const context = useContext(MediaContext)
  const cb = useRef(onAppend)
  useEffect(() => { cb.current = onAppend }, [onAppend])

  useEffect(() => {
    if (!context) { return }
    const unSub = context.onAppendScript(cb.current)
    return () => unSub()
  }, [context])
}

interface SidebarProps {
  selectedMediaId: string | undefined
  selectMedia(id: string): void
}

function Sidebar({ selectedMediaId, selectMedia }: SidebarProps) {
  const triggered = useRef(false)
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const me = useSelector(selectMyData)
  const parked = useSelector(getParkedMedia)
  const held = useSelector(selectHeldCall)

  const media = useSelector(selectMyMedia)
  const wrappingUp = media.filter(m => m.status === CallState.WaitingForWrapup)

  const [mediaMenuState, setMediaMenuState] = useState<MediaMenuState>()

  const isCollapsed = useSelector(s => selectIsPanelCollapsed(s, SidebarPanelKey))
  const dispatch = useDispatch()

  //select the correct side of the blind transfer
  useEffect(() => {
    if (!held) {
      triggered.current = false
    } else if (triggered.current) {
      return
    } else {
      media.forEach(element => {
        if (element.mediaType === 'CALL' && element.id !== held.id) {
          triggered.current = true
          selectMedia(element.id)
          return
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held])

  if (!me || !extension) return null
  //(m.mediaType === "EMAIL" && m.status === CallState.OutboundRinging) parked and then resumed
  const mediaToShow = media.filter(m => m.status === CallState.ExtensionAnswered || m.status === CallState.QueueHold || m.status === CallState.None || ((m.mediaType === "CALL" || m.mediaType === "VM") && m.status !== CallState.WaitingForWrapup) || (m.mediaType === "EMAIL" && m.status === CallState.ExtensionPending && parked.find(x => x === m.id)))

  if (isCollapsed) {
    return <div className='sidebar-collapsed'>
      <button className='collapseButton' onClick={() => dispatch(actions.setPanelCollapsed(SidebarPanelKey, false))}>
        <ExpandIcon />
      </button>
      <div title={me.fullName}>
        <PresenceIcon presence={extension.presence} deviceGroupType={extension.deviceGroupType} />
      </div>
      {[...mediaToShow, ...wrappingUp].map(m => <RenderCollapsedIcon media={m} selected={m.id === selectedMediaId} onClick={(id) => selectMedia(m.id)} />)}
    </div>
  }

  return <div className='sidebar'>
    <button className='collapseButton' onClick={() => dispatch(actions.setPanelCollapsed(SidebarPanelKey, true))}>
      <CollapseIcon />
    </button>
    <div className='agent'>
      <DropdownButton items={[]} className='dropdown'>
        <PresenceIcon presence={extension.presence} deviceGroupType={extension.deviceGroupType} />
        {me.firstname} {me.lastname}
      </DropdownButton>
    </div>
    <div className='media-list'>
      {mediaToShow.map(m =>
        <MediaItem key={m.id} item={m as Media} className={classNames('media', m.id === selectedMediaId && 'selected')} onClick={() => selectMedia(m.id)} icon={MediaItemIconType.white} onMenu={(e) => setMediaMenuState({ target: targetFromEvent(e), items: [m] })} />)}
      {mediaMenuState && <MediaMenu state={mediaMenuState} onClose={() => setMediaMenuState(undefined)} />}
    </div>
    {wrappingUp.length > 0 && <>
      <div className='wraup-calls-header'>
        Wrapups requiring attention
      </div>
      <div className='media-list'>
        {wrappingUp.map(m =>
          <MediaItem key={m.id} item={m as Media} className={classNames('media', m.id === selectedMediaId && 'selected')} onClick={() => selectMedia(m.id)} icon={MediaItemIconType.white} onMenu={(e) => setMediaMenuState({ target: targetFromEvent(e), items: [m] })} />)}
        {mediaMenuState && <MediaMenu state={mediaMenuState} onClose={() => setMediaMenuState(undefined)} />}
      </div>
    </>}
  </div>
}

function RenderCollapsedIcon({ media, selected, onClick }: { media: Media, selected: boolean, onClick?(id: string): void }) {
  const myId = useSelector(selectMyself)
  const devices = useSelector(selectMyDevices)
  const agentLimits = useSelector(s => selectConfigProp(s, 'agentLimits'));
  const now = useSelector(getNow)
  const format = useSelector(selectDateFormating)
  const ref = useRef<HTMLDivElement>(null)

  if (!myId || !devices || !agentLimits || !format) return null

  const device = getActiveDevice(media, devices.devices, devices.primaryDevice)
  const status = agentStatusTitle(now, agentLimits, [media])

  return <>
    <div ref={ref} key={media.id} className={classNames('item', selected && 'selected')}>
      <MediaIcon
        mediaType={media.mediaType} callStatus={media.status}
        onClick={() => { if (onClick) onClick(media.id) }} />
      {media.status === CallState.WaitingForWrapup && <WrapupIcon className='wrapup-overlay' />}
      {status !== '' && <WarningIcon className='warning-overlay' />}
    </div>
    <TitleTag target={ref.current}><MediaItemIconTitle myId={myId} media={media} device={device} status={status} /></TitleTag>
  </>
}

interface MainPanelProps {
  tab: AgentViewTab
  mediaId: string | undefined
}

function MainPanel({ mediaId, tab }: MainPanelProps) {
  const dispatch = useDispatch()
  const media = useSelector(s => mediaId !== undefined && selectMedia(s, mediaId))
  const transcriptionEnabled = useSelector(s => selectConfigProp(s, 'transcriptionEnabled'))
  const transcript = useSelector(s => mediaId !== undefined && hasTranscription(s, mediaId))

  const panel = useMemo(() => {
    if (!mediaId) return

    switch (tab) {
      case 'details':
        {
          return <CallDetailsPage mediaId={mediaId} />
        }
      case 'tasks':
        {
          return <TasksPage mediaId={mediaId} />
        }
      case 'contact':
        {
          return <ContactPage mediaId={mediaId} />
        }
      case 'wrapups':
        {
          return <WrapupsPage mediaId={mediaId} />
        }
      case 'transcription':
        {
          return <TranscriptionDetailsPage mediaId={mediaId} />
        }
      default:
        {
          return <MediaPage mediaId={mediaId} />
        }
    }
  }, [tab, mediaId])

  if (!media || transcript === undefined) {
    return <div className='main'>
      <Dashboard />
    </div>
  }

  const main = <div className='main'>
    <Header media={media} />
    <div className='view'>
      <MediaView media={media} />
    </div>
  </div>

  return <StoredSplitter className='splitter' storeKey='agentView' initialValue={0.66}>
    {main}
    <SplitterDivider />
    <div className='tab'>
      <header>
        <TabsHeader className='tab-buttons'>
          {tabButton('', 'Media', true, false)}
          {tabButton('details', 'More', true, false)}
          {tabButton('tasks', 'History', media && ((media.clidObjectKey === '') || (media.clidObjectKey !== '' && (media.clidObjectType === ClidObjectType.personalContact || media.clidObjectType === ClidObjectType.companyContact || media.clidObjectType === ClidObjectType.customerContact))), false)}
          {tabButton('contact', 'Contact', true, false)}
          {tabButton('wrapups', 'Wrapups', true, media && (media.status === CallState.WaitingForWrapup || (media.mediaType === 'OUT' && media.queueInfo !== undefined && media.queueInfo.forcedWrapup)))}
          {transcriptionEnabled && tabButton('transcription', 'Transcript', true, transcript)}
        </TabsHeader>
      </header>
      <div className='tab-contents'>
        {panel}
      </div>
    </div>
  </StoredSplitter>

  function tabButton(toTab: AgentViewTab, title: string, enabled: boolean, urgent: boolean) {
    return <TabButton active={tab === toTab} disabled={!enabled} onClick={() => {
      if (enabled && media) {
        dispatch(actions.openAgentView(media.id, toTab))
      }
    }} className={classNames(urgent && 'urgent')} >
      {title}
    </TabButton>
  }
}

function Header({ media }: { media: Media }) {
  const myId = useSelector(selectMyself)
  const devices = useSelector(selectMyDevices)
  const dispatch = useDispatch()

  return <header>
    <PhoneCallsIcon />
    <div className='title header-one-bold'>{myId && devices && callStateDescription(media, myId, getActiveDevice(media, devices.devices, devices.primaryDevice))}</div>
    <div className='header-two'>
      {media.mediaType === 'EMAIL' ? 'Waiting time' : 'Current call'}:{' '}
      <span className='header-two-bold'><Timer ts={media.elapsedTime} /></span>
    </div>
    <Button primary className='complete' onClick={() => dispatch(actions.completeMedia(media.id))}>Complete</Button>
  </header>
}

function MediaView({ media }: { media: Media }) {
  if (!media) return null

  if (media.mediaType === "CALL" || media.mediaType === "VM") {
    return <CallView media={media} />
  } else if (media.mediaType === "EMAIL") {
    return <EmailView media={media} />
  } else if (media.mediaType === "TC") {
    return <ChatView media={media} />
  } else if (media.mediaType === "TWEET") {
    return <TweatView tweetId={media.mediaItemId} queueId={media.queueId} reply={false} media={media} />
  } else if (media.mediaType === "OUT") {
    return <OutboundView mediaId={media.id} mediaItemId={media.mediaItemId} />
  } else if (media.mediaType === "FB") {
    return <FacebookView media={media} activityId={media.mediaItemId} queueId={media.queueId} />
  }
  return null
}
